import React, { createContext, useContext } from 'react';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';

interface BreadcrumbItem {
  title: string;
  path?: string;
}

// Context for custom last breadcrumb item
export const BreadcrumbContext = createContext<{ lastItem?: string }>({});
export const useBreadcrumb = () => useContext(BreadcrumbContext);

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items: propItems = [], showHome = true }) => {
  const location = useLocation();
  const { lastItem } = useBreadcrumb();
  
  // Generate breadcrumb items based on current path if no items provided
  const generateBreadcrumbItems = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems: BreadcrumbItem[] = [];
    
    if (showHome) {
      breadcrumbItems.push({ title: 'Home', path: '/dashboard' });
    }
    
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      // Customize titles for better display
      let title = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      
      // Special handling for claims page
      if (segment === 'claims' && pathSegments.length === 1) {
        title = 'Claims Management';
      }
      
      // If this is the last segment and lastItem is set, use it
      if (lastItem && index === pathSegments.length - 1) {
        title = lastItem;
      }
      
      breadcrumbItems.push({ 
        title, 
        path: index === pathSegments.length - 1 ? undefined : currentPath 
      });
    });
    
    return breadcrumbItems;
  };
  
  const breadcrumbItems = propItems.length > 0 ? propItems : generateBreadcrumbItems();
  
  const antdItems = breadcrumbItems.map((item, index) => ({
    key: index,
    title: item.path ? (
      <Link 
        to={item.path} 
        style={{ 
          color: 'white', 
          textDecoration: 'none',
          fontWeight: index === breadcrumbItems.length - 1 ? 'bold' : 'normal'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        {index === 0 && showHome ? <HomeOutlined style={{ marginRight: '4px' }} /> : null}
        {item.title}
      </Link>
    ) : (
      <span style={{ 
        color: 'white', 
        fontWeight: 'bold',
        opacity: 0.9
      }}>
        {item.title}
      </span>
    )
  }));

  return (
    <div style={{ 
      display: 'flex',
      alignItems: 'center'
    }}>
      <AntBreadcrumb
        style={{ color: 'white' }}
        separator={<span style={{ color: 'white' }}>/</span>}
        items={antdItems}
      />
    </div>
  );
};

export default Breadcrumb; 